import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  InputGroup,
  Text,
  VStack,
  Select,
  useDisclosure,
} from '@chakra-ui/react';
import { EmploymentVerification, EmploymentProof as Proof } from "@bluecheck/react-library";
import { ColorResult } from 'react-color';
import { ColorType, CustomStyles, BlueCheckApplication } from '../../../utils/types';
import { useDebounce } from 'src/utils/logs/send-logs';
import { checkMetadataNameExists } from 'src/utils/bluecheck-applications/fetch-bluecheck-applications';
import { FONT_OPTIONS } from 'src/utils/constants';
import ColorPickerVStack from 'src/components/color-picker/color-picker.component';

interface AppDetailsBlueCheckProps {
  handleNext: () => void;
  onUpdate: (metadataName: string) => void;
  editedApp: BlueCheckApplication | null;
}

export const AppDetailsBlueCheck: React.FC<AppDetailsBlueCheckProps> = ({
  handleNext,
  onUpdate,
  editedApp,
}) => {
  const [appName, setAppName] = useState<string>(editedApp ? editedApp.metadataName : '');
  const [appNameError, setAppNameError] = useState<string>('');
  const [isAppNameValid, setIsAppNameValid] = useState<boolean>(editedApp ? true : false);
  const [isNameChecking, setIsNameChecking] = useState<boolean>(false);
  const [appNameInputTouched, setAppNameInputTouched] = useState<boolean>(false);

  const [selectedFont, setSelectedFont] = useState<string>('inter');
  const [showColorPicker, setShowColorPicker] = useState<boolean>(false);
  const [activeColorType, setActiveColorType] = useState<ColorType | ''>('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [proofResponse, setProofResponse] = useState<Proof | null>(null);

  const [customStyles, setCustomStyles] = useState<CustomStyles | null>(null);

  const handleFontChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFont = e.target.value;
    setSelectedFont(newFont);
    setCustomStyles((prev) => ({
      ...prev!,
      fontType: newFont,
    }));
  };

  const debouncedAppName = useDebounce(appName, 500);

  useEffect(() => {
    if (isAppNameValid) {
      onUpdate(appName);
    }
  }, [appName, isAppNameValid]);

  useEffect(() => {
    if (appNameInputTouched && debouncedAppName) {
      validateAndCheckAppName(debouncedAppName);
    }
  }, [debouncedAppName]);

  const handleColorChange = (color: ColorResult, type: ColorType) => {
    setCustomStyles((prev) => ({
      ...prev!,
      [type]: color.hex,
    }));
  };

  const handleColorPickerOpen = (colorType: ColorType) => {
    setActiveColorType(colorType);
    setShowColorPicker(true);
    onOpen();
  };

  const handleColorPickerClose = () => {
    setShowColorPicker(false);
    setActiveColorType('');
    onClose();
  };

  const validateAppName = (name: string): boolean => {
    if (!name.trim()) {
      setAppNameError('App Name is required');
      return false;
    }

    if (name.trim().length < 3) {
      setAppNameError('App Name should contain at least 3 characters');
      return false;
    }

    if (name.trim().length > 50) {
      setAppNameError('App Name should not contain more than 50 characters');
      return false;
    }

    if (!/^[a-zA-Z0-9 ]*$/.test(name)) {
      setAppNameError('App Name should not contain special characters');
      return false;
    }

    if (/^[\d ]/.test(name)) {
      setAppNameError('App Name should not start with a number or space');
      return false;
    }

    setAppNameError('');
    return true;
  };

  const validateAndCheckAppName = async (name: string) => {
    const isValid = validateAppName(name);
    setIsAppNameValid(isValid);

    if (isValid) {
      setIsNameChecking(true);
      const nameExists = await checkMetadataNameExists(name);
      setIsNameChecking(false);

      if (nameExists && !(editedApp && editedApp.metadataName === appName)) {
        setAppNameError('Application name already exists');
        setIsAppNameValid(false);
      } else {
        setAppNameError('');
        setIsAppNameValid(true);
        onUpdate(name);
      }
    }
  };

  const handleAppNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAppName(e.target.value);
    setAppNameInputTouched(true);
    validateAppName(e.target.value);
  };

  const handleNextClick = () => {
    if (isAppNameValid && !isNameChecking) {
      handleNext();
    }
  };

  const handleProofVerified = (proof: Proof) => {
    setProofResponse(proof);
  };

  return (
    <Card
      width="95%"
      variant="outline"
      mt={6}
      backgroundColor="#FFF"
      borderRadius="12px"
      h="fit-content"
    >
      <CardBody p={0}>
        <VStack h="78vh" overflowY="scroll">
          <HStack h="100%" p="36px" w="100%" alignItems="flex-start">
            {/* Left Side: Settings */}
            <VStack w="50%" h="100%" p="36px" spacing="16px" align="flex-start">
              <HStack spacing="12px" alignItems="center">
                <Text fontWeight="bold" fontFamily="inter">
                  {editedApp ? 'Edit' : 'Create a new'} BlueCheck App
                </Text>
              </HStack>
              <FormControl isInvalid={!!appNameError}>
                <Text ml="5px" mb="1px" color="gray.600">
                  Application Name *
                </Text>
                <InputGroup>
                  <Input
                    value={appName}
                    onChange={handleAppNameChange}
                    placeholder="Application Name"
                    required={true}
                    px={5}
                    py={6}
                    borderRadius={16}
                  />
                </InputGroup>
                <FormErrorMessage ml="5px">{appNameError}</FormErrorMessage>
              </FormControl>

              <Box w="100%" mb={4}>
                <Text ml="5px" mb="1px" color="gray.600">
                  Appearance Settings:
                </Text>
                <VStack spacing={4} mt={4} ml={1} align="stretch">
                  <FormControl>
                    <Text mb="1px" color="gray.600">
                      Font Family
                    </Text>
                    <Select value={selectedFont} onChange={handleFontChange}>
                      {FONT_OPTIONS.map((font) => (
                        <option key={font.value} value={font.value}>
                          {font.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <HStack w="100%" justifyContent="space-between" align="stretch">
                      <VStack spacing={3} align="stretch">
                        <ColorPickerVStack
                          label="Background color"
                          colorValue={customStyles?.backgroundColor}
                          colorType="backgroundColor"
                          activeColorType={activeColorType}
                          isOpen={isOpen}
                          onClose={handleColorPickerClose}
                          handleColorPickerOpen={handleColorPickerOpen}
                          handleColorChange={handleColorChange}
                        />

                        <ColorPickerVStack
                          label="Text Color"
                          colorValue={customStyles?.textColor}
                          colorType="textColor"
                          activeColorType={activeColorType}
                          isOpen={isOpen}
                          onClose={handleColorPickerClose}
                          handleColorPickerOpen={handleColorPickerOpen}
                          handleColorChange={handleColorChange}
                        />

                        <ColorPickerVStack
                          label="QR Light color"
                          colorValue={customStyles?.QRLightColor}
                          colorType="QRLightColor"
                          activeColorType={activeColorType}
                          isOpen={isOpen}
                          onClose={handleColorPickerClose}
                          handleColorPickerOpen={handleColorPickerOpen}
                          handleColorChange={handleColorChange}
                        />
                      </VStack>

                      <VStack spacing={3} align="stretch">
                        <ColorPickerVStack
                          label="Border color"
                          colorValue={customStyles?.borderColor}
                          colorType="borderColor"
                          activeColorType={activeColorType}
                          isOpen={isOpen}
                          onClose={handleColorPickerClose}
                          handleColorPickerOpen={handleColorPickerOpen}
                          handleColorChange={handleColorChange}
                        />

                        <ColorPickerVStack
                          label="Border Hover Color"
                          colorValue={customStyles?.borderHoverColor}
                          colorType="borderHoverColor"
                          activeColorType={activeColorType}
                          isOpen={isOpen}
                          onClose={handleColorPickerClose}
                          handleColorPickerOpen={handleColorPickerOpen}
                          handleColorChange={handleColorChange}
                        />

                        <ColorPickerVStack
                          label="QR Dark color"
                          colorValue={customStyles?.QRDarkColor}
                          colorType="QRDarkColor"
                          activeColorType={activeColorType}
                          isOpen={isOpen}
                          onClose={handleColorPickerClose}
                          handleColorPickerOpen={handleColorPickerOpen}
                          handleColorChange={handleColorChange}
                        />
                      </VStack>
                    </HStack>
                  </FormControl>
                </VStack>
              </Box>
              <Box w="100%" mt={4}>
                <Text mb="1px" color="gray.600">
                  Proof Response:
                </Text>
                {proofResponse ? (
                  <Box
                    as="pre"
                    p={2}
                    bg="gray.100"
                    width={'32rem'}
                    height={"20rem"}
                    borderRadius="md"
                    overflowX="auto"
                    fontSize="15px"
                  >
                    {JSON.stringify(proofResponse, null, 2)}
                  </Box>
                ) : (
                  <Text color="gray.500" fontSize="15px">
                    No proof response generated yet.
                  </Text>
                )}
              </Box>
            </VStack>

            <Divider orientation="vertical" />

            <VStack w="50%" h="40%" pl="36px" spacing="16px" align="flex-start">
              <Text fontWeight="bold" fontFamily="inter">
                Preview
              </Text>
              <Divider />
              <EmploymentVerification
                appId="ba923d98-820a-4d2b-930a-a2113b3e92b"
                onProofVerified={handleProofVerified}
                styles={{
                  QRDarkColor: customStyles?.QRDarkColor,
                  QRLightColor: customStyles?.QRLightColor,
                  borderHoverColor: customStyles?.borderHoverColor,
                  backgroundColor: customStyles?.backgroundColor,
                  borderColor: customStyles?.borderColor,
                  textColor: customStyles?.textColor,
                  fontType: customStyles?.fontType,
                }}
              />
            </VStack>
          </HStack>
        </VStack>

        <VStack alignItems="flex-end">
          <Flex
            alignItems="center"
            gap="16px"
            mr="20px"
            mt="20px"
            mb="20px"
            justifyContent="flex-end"
          >
            <Button
              disabled={!isAppNameValid || isNameChecking}
              onClick={handleNextClick}
              _hover={{ transform: 'scale(1.05)' }}
            >
              Next
            </Button>
          </Flex>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default AppDetailsBlueCheck;
